import{ useEffect, useRef, useState } from "react";
import Filled from "./images/filled.png";
import Empty from "./images/empty.png";

interface SpinnerProps {
  width?: number;
  height?: number;
  waveAmplitude?: number;
  wavePoints?: number;
  waveLength?: number;
}

const CustomSpinner = ({
  width = 100,
  height = 100,
  waveAmplitude = 5,
  wavePoints = 40,
  waveLength = 100
}: SpinnerProps) => {

  // STATES

  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isFilled, setIsFilled] = useState(false);

  // REFS

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // FUNCTIONS

  // ----------------------------------------------------------------------------------------
  /**
   * @ES GENERA LA ONDA DE LLENADO DEL SPINNER
   * @EN GENERATE THE WAVE OF THE SPINNER
   * 
   * @param progress 
   * @param phase 
   * @returns 
   */
  // ----------------------------------------------------------------------------------------
  const generateWavePath = (progress: number, phase: number) => {
    let d = `M0,${100 - progress}`;
    for (let i = 0; i <= wavePoints; i++) {
      const x = (i * waveLength) / wavePoints;
      const y =
        100 - progress +
        Math.sin((i / wavePoints) * Math.PI * 2 + phase) * waveAmplitude;
      d += ` L${x},${y}`;
    }
    d += " L100,100 L0,100 Z";
    return d;
  };
  // ----------------------------------------------------------------------------------------

  // USE EFFECTS

  // ----------------------------------------------------------------------------------------
  /**
   * @ES ANIMACION DE LA FASE (ONDA)
   * @EN ANIMATION OF THE PHASE (WAVE)
   */
  // ----------------------------------------------------------------------------------------
  useEffect(() => {

    const duration = 2000; // DURATION OF THE WAVE

    // START ANIMATION

    const animatePhase = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const newPhase = ((elapsed % duration) / duration) * Math.PI * 2;
      setPhase(newPhase);
      requestRef.current = requestAnimationFrame(animatePhase);
    };

    requestRef.current = requestAnimationFrame(animatePhase);

    // CANCEL ANIMATION WHEN UNMOUNTED

    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      startTimeRef.current = null;
    };

  }, []);

  // ----------------------------------------------------------------------------------------
  /**
   * @ES ANIMACION DE LLENADO Y VACIADO
   * @EN ANIMATION OF THE FILLING AND EMPTYING
   */
  // ----------------------------------------------------------------------------------------
  useEffect(() => {

    // VARIABLES

    let start: number | null = null;
    let direction: 'fill' | 'empty' = 'fill';
    let localRequest: number;
    const duration = 3000;
    const startValue = 0;
    const endValue = 90;

    // START ANIMATION

    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;

      // FILL

      if (direction === 'fill') {
        const progressValue = Math.min(
          startValue + (endValue - startValue) * (elapsed / duration),
          endValue
        );
        setProgress(progressValue);
        if (elapsed < duration) {
          localRequest = requestAnimationFrame(animate);
        } else {
          setIsFilled(true);
          timeoutRef.current = setTimeout(() => {
            direction = 'empty';
            start = null;
            setIsFilled(false);
            localRequest = requestAnimationFrame(animate);
          }, 2000); // 3 segundos imagen rellena
        }

      // EMPTY

      } else {
        const progressValue = Math.max(
          endValue - (endValue - startValue) * (elapsed / duration),
          startValue
        );
        setProgress(progressValue);
        if (elapsed < duration) {
          localRequest = requestAnimationFrame(animate);
        } else {
          // Reiniciar ciclo
          direction = 'fill';
          start = null;
          localRequest = requestAnimationFrame(animate);
        }
      }
    };

    localRequest = requestAnimationFrame(animate);

    // CANCEL ANIMATION WHEN UNMOUNTED

    return () => {
      if (localRequest) cancelAnimationFrame(localRequest);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // RENDER

  return (
    <svg width={width} height={height} viewBox="0 0 100 100">

      {/* MASK */}

      <defs>
        <clipPath id="liquid-mask">
          <path d={generateWavePath(progress, phase)} fill="white" />
        </clipPath>
      </defs>

      {/* FILLED IMAGE WITH MASK AND EMPTY IMAGE */}

      {isFilled ? (
        <image href={Filled} width="100" height="100" />
      ) : (
        <image href={Filled} width="100" height="100" clipPath="url(#liquid-mask)" />
      )}

      {/* EMPTY IMAGE */}

      <image href={Empty} width="100" height="100" />
      
    </svg>
  );
};

export default CustomSpinner;